import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { PopoverDimensions, PopoverPlacementType } from "./interface";
import { calculateOverlayDimensions, transformElement } from "./utils";
import { debounce } from "../../utils/debounce";
import { throttle } from "../../utils/throttle";

/**
 * @slot (default) - Content of the popover
 * @slot trigger - The trigger element
 */
@Component({
    tag: "ux-popover",
    styleUrl: "./popover.scss"
})
export class Popover implements ComponentInterface {
    @Element() domNode!: HTMLUxPopoverElement;
    @State() dimensions: PopoverDimensions | null = null;
    @State() isOpened = false;

    private overlayElement: HTMLElement;
    private triggerElement: HTMLElement;

    /** Indicates whether the popover state is controlled by another component */
    @Prop({ reflect: true }) readonly controlled: boolean = false;
    /** Placement of the popover */
    @Prop({ reflect: true }) readonly placement: PopoverPlacementType = "top";
    /** Indicates whether the popover should be open or not */
    @Prop({ mutable: true, reflect: true }) opened = false;
    /** Set to `true` to automatically close the popover when a list item is selected. */
    // eslint-disable-next-line @stencil-community/ban-default-true
    @Prop() readonly closeOnSelect: boolean = true;
    /** The element used to align the popover */
    // @ts-expect-error Check for typing
    @Prop() readonly targetElement: HTMLElement = null;

    @Watch("targetElement")
    onTargetChange(): void {
        if (this.opened) {
            this.updateOverlayPlacementDebounced();
        }
    }

    @Watch("placement")
    onPlacementChange(): void {
        if (this.opened) {
            this.updateOverlayPlacementDebounced();
        }
    }

    @Watch("opened")
    onOpenedChange(): void {
        if (this.opened) {
            this.addEventListeners();
            this.updateOverlayPlacementDebounced();
        } else {
            this.removeEventListeners();
            this.dimensions = null;
            this.isOpened = false;
        }
    }

    @Watch("dimensions")
    onDimensionsChange(): void {
        if (!this.opened || this.dimensions === null) {
            this.isOpened = false;
            return;
        }
        transformElement(this.overlayElement, `${this.dimensions.left}px`, `${this.dimensions.top}px`);
        if (!this.isOpened) {
            window.setTimeout(() => {
                if (!this.opened || this.dimensions === null) {
                    return;
                }
                this.isOpened = true;
            }, 10);
        }
    }

    @Watch("isOpened")
    updateIsOpened(): void {
        if (!this.isOpened) {
            return;
        }
        this.updateOverlayPlacementDebounced();
    }

    /** Fired when the popover opens. */
    @Event() uxShow!: EventEmitter;

    /** Fired when the popover closes. */
    @Event() uxHide!: EventEmitter;

    /** Show the popover */
    @Method()
    async show() {
        if (this.opened) {
            return;
        }
        const event = this.uxShow.emit();
        if (event.defaultPrevented) {
            return;
        }
        this.opened = true;
    }

    /** Hide the popover */
    @Method()
    async hide() {
        if (!this.opened) {
            return;
        }
        const event = this.uxHide.emit();
        if (event.defaultPrevented) {
            return;
        }
        this.opened = false;
    }

    /** Update the popver panel placemnt  */
    @Method()
    async updatePlacement() {
        if (!this.opened) {
            return;
        }
        this.updateOverlayPlacementThrottled();
    }

    componentDidLoad() {
        this.onOpenedChange();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    // -------------------------------------
    // Private methods
    // -------------------------------------

    private updateOverlayPlacement = (): void => {
        const { opened, placement, overlayElement } = this;
        if (!opened || overlayElement === null || overlayElement === undefined) {
            return;
        }
        const targetElement = this.getTargetElement();
        const overlayRect = overlayElement.getBoundingClientRect();
        if (targetElement === null || placement === "center") {
            this.dimensions = {
                left: 0.5 * (window.innerWidth - overlayRect.width),
                top: 0.5 * (window.innerHeight - overlayRect.height),
                width: overlayRect.width,
                height: overlayRect.height,
                placement: "center"
            };
            return;
        }
        const targetRect = targetElement.getBoundingClientRect();
        this.dimensions = calculateOverlayDimensions(targetRect, overlayRect, placement);
    };

    private updateOverlayPlacementDebounced = debounce(this.updateOverlayPlacement, 20);

    private updateOverlayPlacementThrottled = throttle(this.updateOverlayPlacement, 20);

    private addEventListeners = (): void => {
        this.removeEventListeners();
        if (this.controlled) {
            return;
        }
        window.addEventListener("scroll", this.handleViewportChange);
        window.addEventListener("resize", this.handleViewportChange);
        document.addEventListener("mousedown", this.handleDocumentMouseDown);
        document.addEventListener("keydown", this.handleDocumentKeyDown);
        this.overlayElement.addEventListener("uxSelect", this.handleListSelect);
        // @ts-expect-error Check for typing
        this.overlayElement.addEventListener("uxClosePopover", this.handleClosePopover);
    };

    private removeEventListeners = (): void => {
        window.removeEventListener("scroll", this.handleViewportChange);
        window.removeEventListener("resize", this.handleViewportChange);
        document.removeEventListener("mousedown", this.handleDocumentMouseDown);
        document.removeEventListener("keydown", this.handleDocumentKeyDown);
        this.overlayElement.removeEventListener("uxSelect", this.handleListSelect);
        // @ts-expect-error Check for typing
        this.overlayElement.removeEventListener("uxClosePopover", this.handleClosePopover);
    };

    // -------------------------------------
    // Event Handlers
    // -------------------------------------

    private handleViewportChange = () => {
        if (this.opened) {
            this.updateOverlayPlacementThrottled();
        }
    };

    private handleDocumentKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            this.hide();
            return;
        }

        const targetElement = this.getTargetElement();
        if (event.key === "Tab") {
            setTimeout(() => {
                if (document.activeElement && document.activeElement.closest(targetElement.tagName) !== targetElement) {
                    this.hide();
                    return;
                }
            });
        }
        if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
            event.preventDefault();
        }
    };

    private getTargetElement = (): HTMLElement => {
        return this.targetElement instanceof HTMLElement ? this.targetElement : this.triggerElement || null;
    };

    private handleDocumentMouseDown = (event: MouseEvent) => {
        if (!this.getTargetElement().contains(event.target as HTMLElement) && !this.domNode.contains(event.target as HTMLElement)) {
            requestAnimationFrame(() => this.hide());
        }
    };

    private handleKeyDown = (event: KeyboardEvent) => {
        if (!this.opened && ["ArrowDown", "ArrowUp"].includes(event.key)) {
            this.show();
            event.preventDefault();
            event.stopPropagation();
        }
    };

    private handleTriggerClick = () => {
        if (this.opened) {
            this.hide();
        } else {
            this.show();
        }
    };

    private handleClosePopover = (event: CustomEvent) => {
        event.stopPropagation();
        this.hide();
    };

    private handleListSelect = () => {
        if (this.closeOnSelect) {
            this.hide();
        }
    };

    render() {
        const { opened, dimensions, isOpened } = this;
        const baseClass = "ux-popover";
        const placement = dimensions !== null ? dimensions.placement : this.placement;
        return (
            <Host onKeyDown={this.handleKeyDown}>
                {/* @ts-expect-error Check for typing */}
                <div class={`${baseClass}__trigger`} ref={(el) => (this.triggerElement = el)} onClick={this.handleTriggerClick}>
                    <slot name="trigger" />
                </div>
                <div
                    class={{
                        [`${baseClass}__overlay`]: true,
                        [`${baseClass}__overlay--opening`]: opened && !isOpened,
                        [`${baseClass}__overlay--opened`]: opened && isOpened
                    }}
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.overlayElement = el)}
                    data-popover-placement={placement}
                    style={opened ? undefined : { display: "none" }}
                >
                    <div class={`${baseClass}__container`}>
                        <slot />
                    </div>
                </div>
            </Host>
        );
    }
}
